import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Sparkles, Lock, Users, Brain, Check, Star, X, HandHeart, Stethoscope, ChevronDown } from 'lucide-react';
import { usePricing } from '@/hooks/usePricing';

// Authentic urban photography of men in our communities.
// HERO_MAIN: A Black man journaling on a bench outdoors — reflection & growth.
// HERO_SUB_1: A man, urban waterfront/city skyline (Erick Reyes).
// HERO_SUB_2: A group of men laughing together outdoors — brotherhood.
const HERO_MAIN = "https://images.unsplash.com/photo-1494883759339-0b042055a4ee?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
// HERO_SUB_1: A man, urban waterfront/city skyline (Erick Reyes).
const HERO_SUB_1 = "https://images.unsplash.com/photo-1599070306640-28f961d4f89c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const HERO_SUB_2 = "https://images.unsplash.com/photo-1543807535-eceef0bc6599?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
// HERO_SUB_3: A young man in a gray hoodie in the city — every man, every generation.
const HERO_SUB_3 = "https://images.unsplash.com/photo-1611817757591-c3f345024273?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const TRUST = [
  { icon: Users, label: "For men from every background" },
  { icon: Sparkles, label: "Warm, substantive AI coaching — never clinical" },
  { icon: ShieldCheck, label: "Evidence-informed growth & wellness tools" },
  { icon: Brain, label: "Culturally responsive spaces for the stories that shape us" },
  { icon: Lock, label: "Private by design — your journal stays yours" },
];

const STATS = [
  { k: "~80%", v: "Men account for nearly 4 in 5 suicide deaths in the U.S. — often after carrying it alone for years. Early, judgment-free support can change that.", src: "CDC · National Center for Health Statistics", href: "https://www.cdc.gov/suicide/data/index.html", external: true },
  { k: "Half", v: "Men are consistently about half as likely as women to receive mental health treatment — even when they're struggling just as much. Trust and representation help close that gap.", src: "SAMHSA · 2024 NSDUH", href: "https://www.samhsa.gov/data/report/2024-nsduh-detailed-tables", external: true },
  { k: "Stigma", v: "Stigma, cultural expectations, and a shortage of providers who understand a man's world keep too many men from getting help. Brotherhood+ exists to lower those barriers — for every man.", src: "NIMH · Men and Mental Health", href: "https://www.nimh.nih.gov/health/topics/men-and-mental-health", external: true },
];

const PILLARS = [
  { title: "Personalized guidance", body: "An AI coach that speaks the language of men who look like you — direct, respectful, and never clinical." },
  { title: "Practical, everyday tools", body: "Short lessons on stress, money, fatherhood, relationships, sleep — the things that actually run our lives." },
  { title: "Trusted resources", body: "A directory of culturally responsive therapists, crisis support, and community events — one tap away." },
];

const FAQS = [
  { q: "Who is Brotherhood+ for?", a: "Every man ready to grow. Brotherhood+ is rooted in culture and open to every man — the coaching, tools, and community are made for men of every background." },
  { q: "Is this a replacement for therapy?", a: "No. Brotherhood+ is for education, wellness, and self-reflection — not medical or therapeutic advice. If you're in crisis, call or text 988 anytime. Looking for a professional? Our free directory lists culturally responsive, licensed therapists." },
  { q: "What happens during the free trial?", a: "Every new member starts with a 7-day free trial. Explore the AI Coach, sample free course lessons, and see how Brotherhood+ fits — no pressure, and you can cancel anytime before it ends." },
  { q: "How does the AI Coach work?", a: "Think of a wise older brother in your corner — warm, direct, and never clinical. Tell it a little about your background and what you're working on, and it tailors its guidance to you. Your conversations stay private." },
  { q: "Can I use Brotherhood+ on my phone?", a: "Yes. Add it to your home screen from Settings for a full-screen, one-tap experience, or use it in any browser. Your progress follows you wherever you sign in." },
  { q: "Can I cancel anytime?", a: "Absolutely. Plans are simple — month-to-month, or save with annual. You can cancel from your profile whenever you like. No phone calls, no hoops." },
];

function LandingFaq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="max-w-3xl mx-auto px-6 py-20" data-testid="landing-faq">
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3">Real talk · everybody's welcome</p>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light text-navy">
          Q&amp;A.
        </h2>
      </div>
      <div className="space-y-3">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={f.q} className="bg-white rounded-2xl card-soft overflow-hidden" data-testid={`faq-item-${i}`}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                data-testid={`faq-question-${i}`}
                className="w-full flex items-center justify-between gap-4 text-left p-5 sm:p-6 hover:bg-beige/40 transition-smooth"
              >
                <span className="font-heading text-lg text-navy">{f.q}</span>
                <ChevronDown size={18} className={`text-gold shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              <div
                className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                data-testid={`faq-answer-${i}`}
              >
                <div className="overflow-hidden">
                  <p className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-1 text-navy/70 leading-relaxed">{f.a}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-center text-sm text-navy/55 mt-8">
        Still curious? <Link to="/login" className="text-navy font-medium underline underline-offset-4">Create a free account</Link> and explore for yourself.
      </p>
    </section>
  );
}

const PLUS_FEATURES = [
  'Brotherhood University',
  'AI Coach whenever you need it',
  'Daily Wellness Check-ins',
  'Weekly Wellness Reviews',
  'AI Journal + Reflections',
  'Growth Plans',
  'Progress Dashboard',
  'Therapist Directory',
  'Text-to-Speech',
  'Support Guides',
  'Crisis Resources',
];

const SUPPORT_FEATURES = [
  'AI Support Coach',
  'Support Library',
  'Conversation Starters',
  'Practical Guides',
  'Grief & Crisis Resources',
  'Anger & Stress Support',
  'Relationship Guidance',
  'Self-Care Resources',
  'Culturally Responsive Support',
];

const SUPPORT_EXCLUSIONS = [
  'Brotherhood University',
  'Personal Wellness Dashboard',
  'Journals',
  'Assessments',
  'Personal Growth Plans',
];

function LandingBillingToggle({ billing, onChange }) {
  return (
    <div className="inline-flex items-center bg-white border border-navy/10 rounded-full p-1 relative shadow-sm" data-testid="landing-billing-toggle">
      <div
        className={`absolute top-1 bottom-1 rounded-full bg-navy shadow-md transition-all duration-300 ease-out ${
          billing === 'monthly' ? 'left-1 right-1/2 mr-0.5' : 'left-1/2 right-1 ml-0.5'
        }`}
      />
      {['monthly', 'annual'].map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          data-testid={`landing-billing-${v}`}
          className={`relative z-10 px-6 sm:px-8 py-2.5 text-sm font-medium rounded-full transition-colors duration-300 ${
            billing === v ? 'text-white' : 'text-navy/60 hover:text-navy'
          }`}
        >
          {v === 'monthly' ? 'Monthly' : 'Annual'}
        </button>
      ))}
    </div>
  );
}

function LandingPricing() {
  const [billing, setBilling] = useState('monthly');
  const pricing = usePricing();
  const isAnnual = billing === 'annual';
  const iv = isAnnual ? 'annual' : 'monthly';
  const plusPrice = pricing?.member?.[iv]?.amount != null ? String(pricing.member[iv].amount) : (isAnnual ? '99.99' : '14.99');
  const plusPeriod = pricing?.member?.[iv]?.period || (isAnnual ? '/year' : '/month');
  const plusSubcopy = isAnnual ? 'Save 44% · best value' : 'Cancel anytime';
  const supportPrice = pricing?.support?.[iv]?.amount != null ? String(pricing.support[iv].amount) : (isAnnual ? '69.99' : '9.99');
  const supportPeriod = pricing?.support?.[iv]?.period || (isAnnual ? '/year' : '/month');
  const supportSubcopy = isAnnual ? 'Save 42% · best value' : 'For the people who help him do it.';
  const plusRegisterPlan = isAnnual ? 'annual' : 'monthly';
  const supportRegisterPlan = plusRegisterPlan;

  return (
    <section id="pricing" className="bg-white border-t border-navy/5">
      <div className="max-w-[1200px] mx-auto px-6 py-20 lg:py-24" data-testid="landing-pricing">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4">Membership</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light leading-tight">
            Two products.<br/><span className="italic text-navy/60">One brotherhood.</span>
          </h2>
          <p className="text-navy/70 text-lg mt-6 leading-relaxed">
            Brotherhood+ is for the men doing the work. Brotherhood Support is for the people who care about them.
            Pick your path. Cancel anytime.
          </p>
        </div>

        <div className="mt-10 flex justify-center">
          <LandingBillingToggle billing={billing} onChange={setBilling} />
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          {/* Brotherhood+ */}
          <div
            data-testid="landing-plan-plus"
            className="relative rounded-[2rem] p-8 lg:p-10 flex flex-col h-full transition-smooth hover:-translate-y-0.5 bg-navy text-white shadow-[0_20px_60px_rgba(11,25,44,0.28)] ring-2 ring-gold/70"
          >
            {isAnnual && (
              <span
                data-testid="landing-plus-best-value"
                className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy text-[10px] uppercase tracking-[0.2em] font-semibold rounded-full px-4 py-1.5 flex items-center gap-1.5 shadow-md whitespace-nowrap"
              >
                <Star size={12} strokeWidth={2.5} fill="currentColor" />
                Best value · save up to 44%
              </span>
            )}
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-gold">Brotherhood+</p>
            <h3 className="font-heading text-2xl mt-1">For the man doing the work.</h3>
            <p className="text-sm mt-3 leading-relaxed text-white/75">
              For men investing in their mental wellness, emotional growth, and leadership.
            </p>
            <div className="mt-6 min-h-[92px]">
              <div key={`plus-${billing}`} className="animate-fade-in">
                <div className="flex items-baseline gap-1">
                  <span className="text-lg text-white/55">$</span>
                  <span className="font-heading text-5xl lg:text-6xl font-light tracking-tight" data-testid={`landing-plus-amount-${billing}`}>
                    {String(plusPrice).split('.')[0]}
                    {String(plusPrice).includes('.') && <span className="text-3xl lg:text-4xl">.{String(plusPrice).split('.')[1]}</span>}
                  </span>
                  <span className="text-sm ml-1 text-white/60">{plusPeriod}</span>
                </div>
                <p className="text-xs mt-1 text-white/60">{plusSubcopy}</p>
              </div>
            </div>
            <ul className="mt-6 space-y-2.5 flex-grow" data-testid="landing-plus-features">
              {PLUS_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm leading-relaxed">
                  <Check size={16} strokeWidth={2.25} className="mt-0.5 shrink-0 text-gold" />
                  <span className="text-white/90">{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to={`/register?role=member&plan=${plusRegisterPlan}`}
              data-testid="landing-plus-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium bg-gold text-navy hover:bg-[#D4B36F] transition-smooth"
            >
              Start Brotherhood+ <ArrowRight size={14}/>
            </Link>
          </div>

          {/* Brotherhood Support */}
          <div
            data-testid="landing-plan-support"
            className="relative rounded-[2rem] p-8 lg:p-10 flex flex-col h-full transition-smooth hover:-translate-y-0.5 bg-beige text-navy border border-navy/10"
          >
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-gold">Brotherhood Support</p>
            <h3 className="font-heading text-2xl mt-1">For the people who help him do it.</h3>
            <p className="text-sm mt-3 leading-relaxed text-navy/70">
              For spouses, partners, parents, siblings, mentors, pastors, coaches, and friends supporting the men they love.
            </p>
            <div className="mt-6 min-h-[92px]">
              <div key={`support-${billing}`} className="animate-fade-in">
                <div className="flex items-baseline gap-1">
                  <span className="text-lg text-navy/50">$</span>
                  <span className="font-heading text-5xl lg:text-6xl font-light tracking-tight" data-testid={`landing-support-amount-${billing}`}>
                    {String(supportPrice).split('.')[0]}
                    {String(supportPrice).includes('.') && <span className="text-3xl lg:text-4xl">.{String(supportPrice).split('.')[1]}</span>}
                  </span>
                  <span className="text-sm ml-1 text-navy/60">{supportPeriod}</span>
                </div>
                <p className="text-xs mt-1 text-navy/50">{supportSubcopy}</p>
              </div>
            </div>
            <ul className="mt-6 space-y-2.5" data-testid="landing-support-features">
              {SUPPORT_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm leading-relaxed">
                  <Check size={16} strokeWidth={2.25} className="mt-0.5 shrink-0 text-gold" />
                  <span className="text-navy/80">{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-2xl bg-white border border-navy/10 p-4 flex-grow" data-testid="landing-support-exclusions">
              <p className="text-xs text-navy/70 leading-relaxed">
                This membership is designed to help you support someone else. It does <span className="font-semibold text-navy">not</span> include:
              </p>
              <ul className="mt-2 space-y-1.5">
                {SUPPORT_EXCLUSIONS.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-navy/60 leading-relaxed">
                    <X size={12} strokeWidth={2.5} className="mt-0.5 shrink-0 text-navy/40" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <Link
              to={`/register?role=supporter&plan=${supportRegisterPlan}`}
              data-testid="landing-support-cta"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium bg-navy text-white hover:bg-navy-soft transition-smooth"
            >
              Become a Supporter <ArrowRight size={14}/>
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-navy/50 mt-10 max-w-xl mx-auto leading-relaxed">
          Prices in USD. Brotherhood+ never sells your data — see our{' '}
          <a href="/privacy" className="underline underline-offset-2">Privacy Promise</a>.
        </p>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F9F8F6] text-navy animate-fade-in">
      {/* Header */}
      <header className="max-w-[1280px] mx-auto flex items-center justify-between px-4 sm:px-6 py-6 animate-fade-up" data-testid="landing-header" style={{ animationDelay: '40ms', opacity: 0 }}>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-navy text-white flex items-center justify-center font-heading text-xl sm:text-2xl">B<span className="text-gold">+</span></span>
          <span className="font-heading text-xl sm:text-3xl tracking-tight bh-goldshine">Brotherhood<span className="text-gold">+</span></span>
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <Link to="/directory" data-testid="landing-nav-directory" className="hidden sm:inline text-sm text-navy/70 hover:text-navy transition-smooth">Find a Therapist</Link>
          <a href="#pricing" data-testid="landing-pricing-link" className="hidden sm:inline text-sm text-navy/70 hover:text-navy transition-smooth">Pricing</a>
          <Link to="/login?mode=signin" data-testid="landing-signin-btn" className="text-sm text-navy/80 hover:text-navy font-medium transition-smooth">Sign in</Link>
          <Link to="/assessment" data-testid="landing-signup-btn" className="bg-navy text-white text-sm rounded-full px-3.5 sm:px-4 py-2 whitespace-nowrap hover:bg-navy-soft transition-smooth">Get started</Link>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-[1280px] mx-auto px-6 pt-6 sm:pt-12 pb-16 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 animate-fade-up" style={{ animationDelay: '140ms', opacity: 0 }}>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6">
            <Sparkles size={14} /> Rooted in culture, open to every man
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-[68px] tracking-tight font-light leading-[1.02] text-navy">
            Wellness that sees<br/>
            <span className="italic font-normal text-navy">the whole man.</span>
          </h1>
          <p className="mt-6 text-navy/70 text-lg leading-relaxed max-w-xl">
            A space for men to reflect, grow, learn, and connect — with experiences designed for what we share, and thoughtful room for the stories that make us different. <span className="text-navy font-medium">Rooted in culture. Made for every man ready to grow.</span>
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/assessment" data-testid="landing-start-assessment"
              className="inline-flex items-center justify-center gap-2 bg-navy text-white rounded-full px-8 py-4 font-medium hover:bg-navy-soft transition-smooth text-base shadow-[0_10px_30px_rgba(11,25,44,0.25)]">
              Start your 7-day free trial <ArrowRight size={16} />
            </Link>
            <a href="#why" data-testid="landing-explore"
              className="inline-flex items-center justify-center gap-2 bg-white border border-navy/10 text-navy rounded-full px-8 py-4 font-medium hover:bg-beige transition-smooth text-base">
              Explore Brotherhood+
            </a>
          </div>

          <p className="mt-4 inline-flex items-center gap-2 text-sm text-navy/70" data-testid="landing-trial-note">
            <Sparkles size={14} className="text-gold" strokeWidth={2} />
            <span>Begins right after a quick 2-minute check-in — <span className="text-navy font-semibold">no credit card needed.</span></span>
          </p>

          <Link
            to="/register?role=supporter"
            data-testid="landing-supporter-link"
            className="mt-5 inline-flex items-center gap-2 text-sm text-navy/70 hover:text-navy transition-smooth group"
          >
            <HandHeart size={14} className="text-gold" strokeWidth={1.75} />
            <span>Here to support a man in your life? <span className="text-navy font-medium underline underline-offset-4 group-hover:no-underline">Join Brotherhood Support →</span></span>
          </Link>

          <Link
            to="/directory"
            data-testid="landing-directory-link"
            className="mt-3 flex items-center gap-2 text-sm text-navy/70 hover:text-navy transition-smooth group"
          >
            <Stethoscope size={14} className="text-gold" strokeWidth={1.75} />
            <span>Looking for a therapist? <span className="text-navy font-medium underline underline-offset-4 group-hover:no-underline">Browse the free directory →</span></span>
          </Link>

          {/* Trust row */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="trust-indicators">
            {TRUST.map((t, i) => (
              <div key={t.label || i} className="flex items-start gap-2 text-sm text-navy/70">
                <Check className="text-green mt-0.5 shrink-0" size={16} strokeWidth={2}/>
                <span>{t.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hero imagery — collage layout, not a single meditation photo */}
        <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: '260ms', opacity: 0 }}>
          <div className="absolute -inset-6 bg-gold/10 rounded-[3rem] blur-3xl -z-0" />
          <div className="relative grid grid-cols-6 grid-rows-6 gap-3 h-[560px]">
            <img
              src={HERO_MAIN}
              alt="A Black man journaling on a bench outdoors — a grounded moment of reflection"
              className="col-span-3 row-span-6 rounded-[2rem] object-cover w-full h-full card-soft"
              style={{ objectPosition: 'center' }}
            />
            <img
              src={HERO_SUB_2}
              alt="A group of men laughing together outdoors — brotherhood and connection"
              className="col-span-3 row-span-3 rounded-[2rem] object-cover w-full h-full card-soft"
              style={{ objectPosition: 'center' }}
            />
            <img
              src={HERO_SUB_3}
              alt="A young man in a gray hoodie in the city — every man, every generation is welcome"
              className="col-span-3 row-span-3 rounded-[2rem] object-cover w-full h-full card-soft"
              style={{ objectPosition: 'center 30%' }}
            />
          </div>
        </div>
      </section>

      {/* WHY BROTHERHOOD+ */}
      <section id="why" className="bg-white border-y border-navy/5">
        <div className="max-w-[1280px] mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-4">Why Brotherhood+</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-light leading-tight">
              The need is real.<br/>
              <span className="text-navy/50">The opportunity is greater.</span>
            </h2>
            <p className="text-navy/70 text-lg mt-6 leading-relaxed">
              Brotherhood+ is rooted in culture and built to help every man do the inner work. Too many men — especially those from communities that traditional wellness spaces have long overlooked — face real barriers to mental wellness. Not because they are less resilient, but because culturally responsive support has been hard to find. We built trusted guidance, education, community, and practical tools with professionals who understand the journey. And every man committed to his growth is welcome at the table.
            </p>
          </div>

          {/* Stats */}
          <div className="mt-16 grid md:grid-cols-3 gap-6 items-stretch" data-testid="landing-stats">
            {STATS.map((s, i) => {
              const inner = (
                <div className="flex flex-col h-full">
                  <p className="font-heading text-3xl sm:text-[2.5rem] font-light text-navy leading-[1.05] tracking-tight min-h-[3.25rem]">{s.k}</p>
                  <p className="text-[15px] text-navy/70 mt-8 leading-[1.7] flex-grow">{s.v}</p>
                  <p className="text-[10px] text-navy/50 mt-10 uppercase tracking-widest">
                    {s.external ? `Source: ${s.src} ↗` : `${s.src} →`}
                  </p>
                </div>
              );
              const cardClass = "block bg-beige rounded-3xl p-10 sm:p-12 hover:bg-[#EAE5D9] transition-smooth h-full";
              return s.external ? (
                <a key={s.k || i} href={s.href} target="_blank" rel="noreferrer" className={cardClass} data-testid={`stat-card-${i}`}>
                  {inner}
                </a>
              ) : (
                <Link key={s.k || i} to={s.href} className={cardClass} data-testid={`stat-card-${i}`}>
                  {inner}
                </Link>
              );
            })}
          </div>
          <p className="text-sm text-navy/60 mt-8 max-w-3xl leading-relaxed italic">
            Statistics highlight real disparities — not destiny. With early support, trusted relationships, and culturally responsive care, outcomes can improve significantly.
          </p>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="max-w-[1280px] mx-auto px-6 py-20 grid md:grid-cols-3 gap-6" data-testid="landing-pillars">
        {PILLARS.map((p, i) => (
          <div key={p.title || i} className="bg-white rounded-3xl p-8 card-soft">
            <div className="w-10 h-10 rounded-full bg-beige flex items-center justify-center text-gold font-heading text-lg mb-4">{i+1}</div>
            <h3 className="font-heading text-2xl mb-3">{p.title}</h3>
            <p className="text-navy/70 text-sm leading-relaxed">{p.body}</p>
          </div>
        ))}
      </section>

      {/* SECOND HERO IMAGE / EMOTIONAL BEAT */}
      <section className="max-w-[1280px] mx-auto px-6 pb-20">
        <div className="relative rounded-[2.5rem] overflow-hidden card-soft">
          <img src={HERO_SUB_1} alt="A man standing confidently in an urban waterfront setting" loading="lazy" decoding="async" className="w-full h-[420px] object-cover" style={{ objectPosition: '50% 18%' }}/>
          <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/70 to-navy/10"/>
          <div className="absolute inset-0 flex items-end p-10 sm:p-14">
            <div className="max-w-xl text-white">
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3">For every version of you</p>
              <h3 className="font-heading text-3xl sm:text-4xl font-light leading-tight">
                The father. The partner. The professional. The man still figuring it out.
              </h3>
              <p className="text-white/80 mt-4 max-w-md">Brotherhood+ meets you where you are — and helps you build where you&apos;re going.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <LandingPricing />

      {/* FAQ */}
      <LandingFaq />

      {/* FINAL CTA */}
      <section className="max-w-[1280px] mx-auto px-6 pb-16 text-center">
        <h3 className="font-heading text-4xl sm:text-5xl tracking-tight font-light">Your wellness. Your growth. Your future.</h3>
        <p className="text-navy/60 mt-4 max-w-xl mx-auto">Start with a free two-minute check-in, then a 7-day free trial. No credit card. No judgment.</p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/assessment" data-testid="landing-final-cta"
            className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-8 py-4 font-medium hover:bg-navy-soft transition-smooth shadow-[0_10px_30px_rgba(11,25,44,0.25)]">
            Take the Free Wellness Check-In <ArrowRight size={16} />
          </Link>
          <Link to="/login" data-testid="landing-final-create"
            className="inline-flex items-center gap-2 bg-white border border-navy/10 text-navy rounded-full px-8 py-4 font-medium hover:bg-beige transition-smooth">
            Create account
          </Link>
        </div>
      </section>

      <footer className="border-t border-navy/5 py-10 text-center text-xs text-navy/50 space-y-2" data-testid="landing-footer">
        <p>Brotherhood+ · Not a substitute for professional care · If you are in crisis, call or text <a href="tel:988" className="text-navy underline">988</a></p>
        <nav aria-label="Legal" className="flex items-center justify-center gap-4 flex-wrap">
          <Link to="/privacy" className="text-navy/70 hover:text-navy underline underline-offset-2" data-testid="footer-privacy-link">Privacy Policy</Link>
          <span aria-hidden className="text-navy/30">·</span>
          <Link to="/terms" className="text-navy/70 hover:text-navy underline underline-offset-2" data-testid="footer-terms-link">Terms of Service</Link>
          <span aria-hidden className="text-navy/30">·</span>
          <Link to="/trust" className="text-navy/70 hover:text-navy underline underline-offset-2">Trust &amp; Safety</Link>
        </nav>
        <p className="text-navy/60 font-medium">© 2026 Renewing Minds Counseling and Consulting Services, LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}
